import { useState, useRef, useCallback, useEffect } from 'react';
import type { RefObject, MouseEvent } from 'react';
import type {
  EditorTool, Annotation, Point,
  PathAnnotation, ShapeAnnotation, LineAnnotation,
  BlurAnnotation, TextAnnotation, PinAnnotation,
  TextInputState, PinStyle,
} from './types';

const MAX_HISTORY = 30;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function uid(): string
{
  return Math.random().toString(36).slice(2, 11);
}

function numberToLetter(num: number, uppercase: boolean): string
{
  let result = '';
  let n = num;
  while (n > 0)
  {
    n--;
    const char = String.fromCharCode((uppercase ? 65 : 97) + (n % 26));
    result = char + result;
    n = Math.floor(n / 26);
  }
  return result || (uppercase ? 'A' : 'a');
}

function letterToNumber(letter: string): number
{
  let result = 0;
  const upperLetter = letter.toUpperCase();
  for (let i = 0; i < upperLetter.length; i++)
  {
    result = result * 26 + (upperLetter.charCodeAt(i) - 64);
  }
  return result;
}

function incrementPinValue(current: string, style: PinStyle): string
{
  if (style === 'numbers')
  {
    const num = parseInt(current, 10);
    return isNaN(num) ? '1' : String(num + 1);
  }
  else
  {
    const num = letterToNumber(current);
    return numberToLetter(num + 1, style === 'letters-upper');
  }
}

function getInitialPinValue(style: PinStyle): string
{
  if (style === 'numbers') return '1';
  if (style === 'letters-upper') return 'A';
  return 'a';
}

// Convert mouse event to SVG coordinate space
function getSvgPoint(e: MouseEvent<SVGElement>, svg: SVGSVGElement): Point
{
  const rect = svg.getBoundingClientRect();
  const scaleX = svg.viewBox.baseVal.width / rect.width;
  const scaleY = svg.viewBox.baseVal.height / rect.height;

  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY,
  };
}

// Clamp point to image boundaries
function clampPoint(point: Point, width: number, height: number): Point
{
  return {
    x: Math.max(0, Math.min(width, point.x)),
    y: Math.max(0, Math.min(height, point.y)),
  };
}

// ---------------------------------------------------------------------------
// Annotation hit test (for eraser and move)
// ---------------------------------------------------------------------------

function findAnnotationAt(point: Point, annotations: Annotation[]): { annotation: Annotation; index: number } | null
{
  // Search in reverse order (top to bottom) to find the topmost element
  for (let i = annotations.length - 1; i >= 0; i--)
  {
    const a = annotations[i];
    if (!a) continue;
    if (annotationHitTest(point, a, 15))
    {
      return { annotation: a, index: i };
    }
  }
  return null;
}

function annotationHitTest(point: Point, a: Annotation, radius: number): boolean
{
  if (a.kind === 'path')
  {
    for (const p of a.points)
    {
      if ((point.x - p.x) ** 2 + (point.y - p.y) ** 2 <= radius ** 2) return true;
    }
    return false;
  }

  if (a.kind === 'shape' || a.kind === 'blur')
  {
    const { x, y, width, height } = a;
    const minX = Math.min(x, x + width) - radius;
    const maxX = Math.max(x, x + width) + radius;
    const minY = Math.min(y, y + height) - radius;
    const maxY = Math.max(y, y + height) + radius;
    return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
  }

  if (a.kind === 'line')
  {
    const { x1, y1, x2, y2 } = a;
    const dx = x2 - x1, dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return false;
    const t = Math.max(0, Math.min(1, ((point.x - x1) * dx + (point.y - y1) * dy) / lenSq));
    return (point.x - (x1 + t * dx)) ** 2 + (point.y - (y1 + t * dy)) ** 2 <= radius ** 2;
  }

  if (a.kind === 'text' || a.kind === 'pin')
  {
    return (point.x - a.x) ** 2 + (point.y - a.y) ** 2 <= (radius * 3) ** 2;
  }

  return false;
}

// ---------------------------------------------------------------------------
// Canvas rendering helpers (for export)
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, alpha: number): string
{
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number,
  x2: number, y2: number,
  size: number,
  color: string,
): void
{
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const a = Math.PI / 6;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - size * Math.cos(angle - a), y2 - size * Math.sin(angle - a));
  ctx.lineTo(x2 - size * Math.cos(angle + a), y2 - size * Math.sin(angle + a));
  ctx.closePath();
  ctx.fill();
}

function renderAnnotationToCanvas(
  ctx: CanvasRenderingContext2D,
  a: Annotation,
  bgCanvas: HTMLCanvasElement | null,
): void
{
  ctx.save();
  ctx.globalAlpha = a.opacity;

  switch (a.kind)
  {
    case 'path':
    {
      if (a.points.length < 2) break;
      const first = a.points[0];
      if (!first) break;
      const isHighlighter = a.tool === 'highlighter';
      ctx.strokeStyle = isHighlighter ? hexToRgba(a.color, 0.45) : a.color;
      ctx.lineWidth = isHighlighter ? a.lineWidth * 4 : a.lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < a.points.length - 1; i++)
      {
        const cur = a.points[i];
        const next = a.points[i + 1];
        if (!cur || !next) continue;
        const mx = (cur.x + next.x) / 2;
        const my = (cur.y + next.y) / 2;
        ctx.quadraticCurveTo(cur.x, cur.y, mx, my);
      }
      const last = a.points[a.points.length - 1];
      if (last) ctx.lineTo(last.x, last.y);
      ctx.stroke();
      break;
    }

    case 'shape':
    {
      const { x, y, width, height } = a;
      ctx.strokeStyle = a.color;
      ctx.lineWidth = a.lineWidth;

      if (a.tool === 'censor')
      {
        ctx.fillStyle = a.color;
        ctx.fillRect(x, y, width, height);
        break;
      }

      if (a.tool === 'circle')
      {
        const rx = Math.abs(width / 2);
        const ry = Math.abs(height / 2);
        const cx = x + width / 2;
        const cy = y + height / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        if (a.fill)
        {
          ctx.fillStyle = hexToRgba(a.color, 0.3);
          ctx.fill();
        }
        ctx.stroke();
      }
      else
      {
        // rect
        if (a.fill)
        {
          ctx.fillStyle = hexToRgba(a.color, 0.3);
          ctx.fillRect(x, y, width, height);
        }
        ctx.strokeRect(x, y, width, height);
      }
      break;
    }

    case 'line':
    {
      const { x1, y1, x2, y2 } = a;
      ctx.strokeStyle = a.color;
      ctx.lineWidth = a.lineWidth;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      if (a.tool === 'arrow')
      {
        ctx.globalAlpha = a.opacity;
        drawArrowHead(ctx, x1, y1, x2, y2, Math.max(10, a.lineWidth * 4), a.color);
      }
      break;
    }

    case 'blur':
    {
      if (!bgCanvas) break;
      const { x, y, width, height } = a;
      const absW = Math.abs(width);
      const absH = Math.abs(height);
      const rectX = width < 0 ? x + width : x;
      const rectY = height < 0 ? y + height : y;
      if (absW < 2 || absH < 2) break;

      // Get the region to process
      const startX = Math.max(0, Math.floor(rectX));
      const startY = Math.max(0, Math.floor(rectY));
      const endX = Math.min(bgCanvas.width, Math.ceil(rectX + absW));
      const endY = Math.min(bgCanvas.height, Math.ceil(rectY + absH));
      const w = endX - startX;
      const h = endY - startY;
      if (w <= 0 || h <= 0) break;

      try
      {
        // Get image data
        const srcCtx = bgCanvas.getContext('2d');
        if (!srcCtx) break;
        const imageData = srcCtx.getImageData(startX, startY, w, h);
        const data = imageData.data;

        // Apply pixelation effect with larger blocks
        const pixelSize = Math.max(18, Math.floor(Math.min(w, h) / 12));

        for (let y = 0; y < h; y += pixelSize)
        {
          for (let x = 0; x < w; x += pixelSize)
          {
            // Get average color of this block
            let r = 0, g = 0, b = 0, count = 0;

            for (let dy = 0; dy < pixelSize && y + dy < h; dy++)
            {
              for (let dx = 0; dx < pixelSize && x + dx < w; dx++)
              {
                const i = ((y + dy) * w + (x + dx)) * 4;
                r += data[i] ?? 0;
                g += data[i + 1] ?? 0;
                b += data[i + 2] ?? 0;
                count++;
              }
            }

            // Apply averaged color with slight brightness and strong noise
            const avgR = (r / count) * 1.15 + 20;
            const avgG = (g / count) * 1.15 + 20;
            const avgB = (b / count) * 1.15 + 20;

            for (let dy = 0; dy < pixelSize && y + dy < h; dy++)
            {
              for (let dx = 0; dx < pixelSize && x + dx < w; dx++)
              {
                const i = ((y + dy) * w + (x + dx)) * 4;
                // Strong noise for glass crystal effect
                const noise = (Math.random() - 0.5) * 40;
                data[i] = Math.max(0, Math.min(255, avgR + noise));
                data[i + 1] = Math.max(0, Math.min(255, avgG + noise));
                data[i + 2] = Math.max(0, Math.min(255, avgB + noise));
              }
            }
          }
        }

        // Put processed data back
        ctx.putImageData(imageData, startX, startY);

        // White overlay for better coverage - increased opacity
        ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
        ctx.fillRect(startX, startY, w, h);
      }
      catch (err)
      {
        // Fallback: simple white rectangle
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.fillRect(rectX, rectY, absW, absH);
      }

      break;
    }

    case 'text':
    {
      const { x, y, text, fontSize, bold, italic, color, hasBackground, backgroundColor } = a;
      ctx.fillStyle = color;
      ctx.font = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${fontSize}px sans-serif`;
      ctx.textBaseline = 'top';
      const metrics = ctx.measureText(text);
      const boxH = fontSize * 1.3;
      if (hasBackground)
      {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(x - 2, y - 2, metrics.width + 4, boxH + 4);
      }
      ctx.fillStyle = color;
      ctx.fillText(text, x, y);
      ctx.textBaseline = 'alphabetic';
      break;
    }

    case 'pin':
    {
      const { x, y, label, color } = a;
      const r = 14;
      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = 4;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x, y);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      break;
    }
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseImageEditorReturn {
  svgRef: RefObject<SVGSVGElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  wrapperRef: RefObject<HTMLDivElement | null>;
  imgRef: RefObject<HTMLImageElement | null>;
  imageWidth: number;
  imageHeight: number;
  imageLoaded: boolean;
  imageSrc: string;
  isExporting: boolean;
  useCrossOrigin: boolean;

  tool: EditorTool;
  setTool: (t: EditorTool) => void;
  color: string;
  setColor: (c: string) => void;
  lineWidth: number;
  setLineWidth: (w: number) => void;
  opacity: number;
  setOpacity: (o: number) => void;
  fillShape: boolean;
  setFillShape: (f: boolean) => void;
  fontSize: number;
  setFontSize: (s: number) => void;
  textBold: boolean;
  setTextBold: (b: boolean) => void;
  textItalic: boolean;
  setTextItalic: (i: boolean) => void;
  textHasBackground: boolean;
  setTextHasBackground: (h: boolean) => void;
  textBackgroundColor: string;
  setTextBackgroundColor: (c: string) => void;

  annotations: Annotation[];
  previewAnnotation: Annotation | null;
  selectedAnnotationIndex: number | null;

  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  showAnnotations: boolean;
  toggleAnnotations: () => void;
  exportImage: () => Promise<string | null>;
  flipHorizontal: () => void;
  flipVertical: () => void;

  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  fitToScreen: () => void;

  pinCounter: number;
  pinStyle: PinStyle;
  pinValue: string;
  setPinStyle: (style: PinStyle) => void;
  setPinValue: (value: string) => void;
  resetPinValue: () => void;
  textInput: TextInputState | null;
  confirmText: (text: string) => void;
  cancelText: () => void;

  onMouseDown: (e: MouseEvent<SVGElement>) => void;
  onMouseMove: (e: MouseEvent<SVGElement>) => void;
  onMouseUp: (e: MouseEvent<SVGElement>) => void;
  onMouseLeave: (e: MouseEvent<SVGElement>) => void;

  flipState: { h: boolean; v: boolean };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useImageEditor(imageSrc: string): UseImageEditorReturn
{
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Image state
  const imageRef = useRef<HTMLImageElement | null>(null);
  const flipRef = useRef({ h: false, v: false });
  const [flipState, setFlipState] = useState({ h: false, v: false });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [useCrossOrigin, setUseCrossOrigin] = useState(true);

  // Tool state
  const [tool, setTool] = useState<EditorTool>('hand');
  const [color, setColor] = useState('#EF4444');
  const [lineWidth, setLineWidth] = useState(3);
  const [opacity, setOpacity] = useState(1);
  const [fillShape, setFillShape] = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [textHasBackground, setTextHasBackground] = useState(false);
  const [textBackgroundColor, setTextBackgroundColor] = useState('#1e1e2e');

  // Zoom/pan state
  const [zoom, setZoom] = useState(1);
  const isPanningRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);

  // Annotation state
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [previewAnnotation, setPreviewAnnotation] = useState<Annotation | null>(null);
  const [selectedAnnotationIndex, setSelectedAnnotationIndex] = useState<number | null>(null);
  const historyRef = useRef<Annotation[][]>([]);
  const futureRef = useRef<Annotation[][]>([]);
  const [pinCounter, setPinCounter] = useState(1);
  const [pinStyle, setPinStyleState] = useState<PinStyle>('numbers');
  const [pinValue, setPinValueState] = useState('1');

  // Drawing state (all refs to avoid stale closures in event handlers)
  const isDrawingRef = useRef(false);
  const startPointRef = useRef<Point | null>(null);
  const currentPathRef = useRef<Point[]>([]);
  const mouseUpProcessedRef = useRef(false);

  // Move/select state
  const moveOffsetRef = useRef<Point | null>(null);
  const isMovingRef = useRef(false);

  // Text input state
  const [textInput, setTextInput] = useState<TextInputState | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // ---------------------------------------------------------------------------
  // Image loading with CORS fallback
  // ---------------------------------------------------------------------------

  useEffect(() =>
  {
    if (!imageSrc) return;
    let cancelled = false;
    const img = new Image();

    if (useCrossOrigin)
    {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () =>
    {
      if (cancelled) return;
      imageRef.current = img;
      setImageWidth(img.naturalWidth);
      setImageHeight(img.naturalHeight);
      setImageLoaded(true);
      // Fit to screen after a small delay to ensure container is rendered
      setTimeout(() =>
      {
        if (!cancelled)
        {
          const container = containerRef.current;
          if (container && img.naturalWidth > 0 && img.naturalHeight > 0)
          {
            const availableWidth = container.clientWidth - 40;
            const availableHeight = container.clientHeight - 40;
            const zoomX = availableWidth / img.naturalWidth;
            const zoomY = availableHeight / img.naturalHeight;
            const initialZoom = Math.min(zoomX, zoomY, 1);
            setZoom(initialZoom);
          }
        }
      }, 100);
    };

    img.onerror = () =>
    {
      if (cancelled) return;
      if (useCrossOrigin)
      {
        setUseCrossOrigin(false);
      }
    };

    img.src = imageSrc;
    return () => { cancelled = true; };
  }, [imageSrc, useCrossOrigin]);

  // ---------------------------------------------------------------------------
  // History helpers
  // ---------------------------------------------------------------------------

  const saveHistory = useCallback((prev: Annotation[]) =>
  {
    historyRef.current = [...historyRef.current, prev].slice(-MAX_HISTORY);
    futureRef.current = [];
  }, []);

  const commitAnnotation = useCallback((a: Annotation) =>
  {
    setAnnotations(prev =>
    {
      saveHistory(prev);
      return [...prev, a];
    });
    setPreviewAnnotation(null);
  }, [saveHistory]);

  const undo = useCallback(() =>
  {
    if (historyRef.current.length === 0) return;
    const previous = historyRef.current[historyRef.current.length - 1];
    if (!previous) return;
    historyRef.current = historyRef.current.slice(0, -1);
    setAnnotations(current =>
    {
      futureRef.current = [current, ...futureRef.current];
      return previous;
    });
  }, []);

  const redo = useCallback(() =>
  {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current[0];
    if (!next) return;
    futureRef.current = futureRef.current.slice(1);
    setAnnotations(current =>
    {
      historyRef.current = [...historyRef.current, current];
      return next;
    });
  }, []);

  const reset = useCallback(() =>
  {
    setAnnotations(prev =>
    {
      saveHistory(prev);
      return [];
    });
    setPinCounter(1);
    setPinValueState(getInitialPinValue(pinStyle));
    setPreviewAnnotation(null);
  }, [saveHistory, pinStyle]);

  // ---------------------------------------------------------------------------
  // Flip
  // ---------------------------------------------------------------------------

  const flipHorizontal = useCallback(() =>
  {
    flipRef.current = { ...flipRef.current, h: !flipRef.current.h };
    setFlipState({ ...flipRef.current });
  }, []);

  const flipVertical = useCallback(() =>
  {
    flipRef.current = { ...flipRef.current, v: !flipRef.current.v };
    setFlipState({ ...flipRef.current });
  }, []);

  // ---------------------------------------------------------------------------
  // Zoom/Pan
  // ---------------------------------------------------------------------------

  const zoomIn = useCallback(() =>
  {
    setZoom(prev => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() =>
  {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  }, []);

  const zoomReset = useCallback(() =>
  {
    setZoom(1);
  }, []);

  const fitToScreen = useCallback(() =>
  {
    const container = containerRef.current;
    if (!container || imageWidth === 0 || imageHeight === 0) return;

    // Get available space (subtract padding)
    const availableWidth = container.clientWidth - 40; // 20px padding on each side
    const availableHeight = container.clientHeight - 40;

    // Calculate zoom to fit both width and height
    const zoomX = availableWidth / imageWidth;
    const zoomY = availableHeight / imageHeight;
    const newZoom = Math.min(zoomX, zoomY, 1); // Don't zoom in beyond 100%

    setZoom(newZoom);
  }, [imageWidth, imageHeight]);

  // ---------------------------------------------------------------------------
  // Export using manual canvas rendering (CORS-free)
  // ---------------------------------------------------------------------------

  const exportImage = useCallback(async (): Promise<string | null> =>
  {
    const img = imgRef.current;

    if (!img || !imageLoaded || imageWidth === 0 || imageHeight === 0)
    {
      return null;
    }

    try
    {
      setIsExporting(true);

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = imageWidth;
      exportCanvas.height = imageHeight;

      const ctx = exportCanvas.getContext('2d');
      if (!ctx) return null;

      ctx.save();
      if (flipRef.current.h || flipRef.current.v)
      {
        ctx.translate(
          flipRef.current.h ? imageWidth : 0,
          flipRef.current.v ? imageHeight : 0,
        );
        ctx.scale(
          flipRef.current.h ? -1 : 1,
          flipRef.current.v ? -1 : 1,
        );
      }
      ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
      ctx.restore();

      for (const annotation of annotations)
      {
        if (annotation)
        {
          try
          {
            renderAnnotationToCanvas(ctx, annotation, exportCanvas);
          }
          catch
          {
            // Skip failed annotations
          }
        }
      }

      const dataUrl = exportCanvas.toDataURL('image/jpeg', 0.95);
      return dataUrl;
    }
    catch
    {
      return null;
    }
    finally
    {
      setIsExporting(false);
    }
  }, [imageLoaded, imageWidth, imageHeight, annotations]);

  // ---------------------------------------------------------------------------
  // Toggle annotations
  // ---------------------------------------------------------------------------

  const toggleAnnotations = useCallback(() =>
  {
    setShowAnnotations(prev => !prev);
  }, []);

  // ---------------------------------------------------------------------------
  // Text tool
  // ---------------------------------------------------------------------------

  const confirmText = useCallback((text: string) =>
  {
    if (!textInput || !text.trim())
    {
      setTextInput(null);
      return;
    }
    const a: TextAnnotation = {
      id: uid(),
      kind: 'text',
      tool: 'text',
      x: textInput.canvasX,
      y: textInput.canvasY,
      text: text.trim(),
      color,
      lineWidth,
      opacity,
      fontSize,
      bold: textBold,
      italic: textItalic,
      hasBackground: textHasBackground,
      backgroundColor: textBackgroundColor,
    };
    commitAnnotation(a);
    setTextInput(null);
  }, [textInput, color, lineWidth, opacity, fontSize, textBold, textItalic, textHasBackground, textBackgroundColor, commitAnnotation]);

  const cancelText = useCallback(() =>
  {
    setTextInput(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Pin customization
  // ---------------------------------------------------------------------------

  const setPinStyle = useCallback((style: PinStyle) =>
  {
    const initialValue = getInitialPinValue(style);
    setPinStyleState(style);
    setPinValueState(initialValue);
  }, []);

  const setPinValue = useCallback((value: string) =>
  {
    setPinValueState(value);
  }, []);

  const resetPinValue = useCallback(() =>
  {
    const initialValue = getInitialPinValue(pinStyle);
    setPinValueState(initialValue);
  }, [pinStyle]);

  // ---------------------------------------------------------------------------
  // Mouse event handlers
  // ---------------------------------------------------------------------------

  const onMouseDown = useCallback((e: MouseEvent<SVGElement>) =>
  {
    const svg = svgRef.current;
    if (!svg) return;
    const rawPt = getSvgPoint(e, svg);
    const pt = clampPoint(rawPt, imageWidth, imageHeight);

    if (tool === 'hand')
    {
      const container = containerRef.current;
      if (!container) return;
      isPanningRef.current = true;
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      return;
    }

    if (tool === 'move')
    {
      const found = findAnnotationAt(pt, annotations);
      if (found)
      {
        setSelectedAnnotationIndex(found.index);
        isMovingRef.current = true;
        const a = found.annotation;

        // Calculate offset based on annotation type
        if (a.kind === 'text' || a.kind === 'pin')
        {
          moveOffsetRef.current = { x: pt.x - a.x, y: pt.y - a.y };
        }
        else if (a.kind === 'shape' || a.kind === 'blur')
        {
          moveOffsetRef.current = { x: pt.x - a.x, y: pt.y - a.y };
        }
        else if (a.kind === 'line')
        {
          moveOffsetRef.current = { x: pt.x - a.x1, y: pt.y - a.y1 };
        }
        else if (a.kind === 'path')
        {
          const first = a.points[0];
          if (first)
          {
            moveOffsetRef.current = { x: pt.x - first.x, y: pt.y - first.y };
          }
        }
      }
      return;
    }

    if (tool === 'text')
    {
      const rect = svg.getBoundingClientRect();
      setTextInput({
        canvasX: pt.x,
        canvasY: pt.y,
        screenX: e.clientX - rect.left,
        screenY: e.clientY - rect.top,
      });
      return;
    }

    if (tool === 'pin')
    {
      const a: PinAnnotation = {
        id: uid(),
        kind: 'pin',
        tool: 'pin',
        x: pt.x,
        y: pt.y,
        number: pinCounter,
        label: pinValue,
        color,
        lineWidth,
        opacity,
      };
      commitAnnotation(a);
      setPinCounter(c => c + 1);
      setPinValueState(v => incrementPinValue(v, pinStyle));
      return;
    }

    isDrawingRef.current = true;
    startPointRef.current = pt;
    mouseUpProcessedRef.current = false;

    if (tool === 'pencil' || tool === 'highlighter')
    {
      currentPathRef.current = [pt];
    }
  }, [tool, color, lineWidth, opacity, pinCounter, pinValue, pinStyle, annotations, commitAnnotation]);

  const onMouseMove = useCallback((e: MouseEvent<SVGElement>) =>
  {
    if (isPanningRef.current && panStartRef.current)
    {
      const container = containerRef.current;
      if (!container) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      container.scrollLeft = panStartRef.current.scrollLeft - dx;
      container.scrollTop = panStartRef.current.scrollTop - dy;
      return;
    }

    const svg = svgRef.current;
    if (!svg) return;
    const rawPt = getSvgPoint(e, svg);
    const pt = clampPoint(rawPt, imageWidth, imageHeight);

    if (isMovingRef.current && selectedAnnotationIndex !== null && moveOffsetRef.current)
    {
      const offset = moveOffsetRef.current;
      const a = annotations[selectedAnnotationIndex];
      if (!a) return;

      const newX = pt.x - offset.x;
      const newY = pt.y - offset.y;

      let movedAnnot: Annotation | null = null;

      if (a.kind === 'text')
      {
        movedAnnot = { ...a, x: newX, y: newY };
      }
      else if (a.kind === 'pin')
      {
        movedAnnot = { ...a, x: newX, y: newY };
      }
      else if (a.kind === 'shape')
      {
        movedAnnot = { ...a, x: newX, y: newY };
      }
      else if (a.kind === 'blur')
      {
        movedAnnot = { ...a, x: newX, y: newY };
      }
      else if (a.kind === 'line')
      {
        const dx = newX - a.x1;
        const dy = newY - a.y1;
        movedAnnot = { ...a, x1: newX, y1: newY, x2: a.x2 + dx, y2: a.y2 + dy };
      }
      else if (a.kind === 'path')
      {
        const first = a.points[0];
        if (first)
        {
          const dx = newX - first.x;
          const dy = newY - first.y;
          movedAnnot = { ...a, points: a.points.map(p => ({ x: p.x + dx, y: p.y + dy })) };
        }
      }

      if (movedAnnot)
      {
        setPreviewAnnotation(movedAnnot);
      }
      return;
    }

    if (!isDrawingRef.current) return;
    const start = startPointRef.current;
    if (!start) return;

    if (tool === 'eraser')
    {
      const eraserRadius = lineWidth * 4;
      setAnnotations(prev =>
      {
        const filtered = prev.filter(a => !annotationHitTest(pt, a, eraserRadius));
        if (filtered.length !== prev.length)
        {
          saveHistory(prev);
          return filtered;
        }
        return prev;
      });
      return;
    }

    if (tool === 'pencil' || tool === 'highlighter')
    {
      currentPathRef.current = [...currentPathRef.current, pt];
      const preview: PathAnnotation = {
        id: '__preview__',
        kind: 'path',
        tool: tool as 'pencil' | 'highlighter',
        points: currentPathRef.current,
        color,
        lineWidth,
        opacity,
      };
      setPreviewAnnotation(preview);
      return;
    }

    // Shape preview
    let preview: Annotation | null = null;

    if (tool === 'rect')
    {
      preview = {
        id: '__preview__', kind: 'shape', tool: 'rect',
        x: start.x, y: start.y,
        width: pt.x - start.x, height: pt.y - start.y,
        fill: fillShape, color, lineWidth, opacity,
      } as ShapeAnnotation;
    }
    else if (tool === 'censor')
    {
      preview = {
        id: '__preview__', kind: 'shape', tool: 'censor',
        x: start.x, y: start.y,
        width: pt.x - start.x, height: pt.y - start.y,
        fill: true, color, lineWidth, opacity,
      } as ShapeAnnotation;
    }
    else if (tool === 'circle')
    {
      preview = {
        id: '__preview__', kind: 'shape', tool: 'circle',
        x: start.x, y: start.y,
        width: pt.x - start.x, height: pt.y - start.y,
        fill: fillShape, color, lineWidth, opacity,
      } as ShapeAnnotation;
    }
    else if (tool === 'line')
    {
      preview = {
        id: '__preview__', kind: 'line', tool: 'line',
        x1: start.x, y1: start.y, x2: pt.x, y2: pt.y,
        color, lineWidth, opacity,
      } as LineAnnotation;
    }
    else if (tool === 'arrow')
    {
      preview = {
        id: '__preview__', kind: 'line', tool: 'arrow',
        x1: start.x, y1: start.y, x2: pt.x, y2: pt.y,
        color, lineWidth, opacity,
      } as LineAnnotation;
    }
    else if (tool === 'blur')
    {
      preview = {
        id: '__preview__', kind: 'blur', tool: 'blur',
        x: start.x, y: start.y,
        width: pt.x - start.x, height: pt.y - start.y,
        color, lineWidth, opacity,
      } as BlurAnnotation;
    }

    setPreviewAnnotation(preview);
  }, [tool, color, lineWidth, opacity, fillShape, annotations, selectedAnnotationIndex, saveHistory]);

  const onMouseUp = useCallback((e: MouseEvent<SVGElement>) =>
  {
    if (isPanningRef.current)
    {
      isPanningRef.current = false;
      panStartRef.current = null;
      return;
    }

    if (isMovingRef.current && selectedAnnotationIndex !== null && previewAnnotation)
    {
      setAnnotations(prev =>
      {
        saveHistory(prev);
        const updated = [...prev];
        updated[selectedAnnotationIndex] = previewAnnotation;
        return updated;
      });

      isMovingRef.current = false;
      setSelectedAnnotationIndex(null);
      moveOffsetRef.current = null;
      setPreviewAnnotation(null);
      return;
    }

    if (!isDrawingRef.current) return;
    if (mouseUpProcessedRef.current) return;
    mouseUpProcessedRef.current = true;
    isDrawingRef.current = false;

    const svg = svgRef.current;
    if (!svg) return;
    const rawPt = getSvgPoint(e, svg);
    const pt = clampPoint(rawPt, imageWidth, imageHeight);
    const start = startPointRef.current;
    if (!start) return;

    if (tool === 'eraser')
    {
      setPreviewAnnotation(null);
      return;
    }

    if (tool === 'pencil' || tool === 'highlighter')
    {
      const points = [...currentPathRef.current, pt];
      if (points.length >= 2)
      {
        const a: PathAnnotation = {
          id: uid(),
          kind: 'path',
          tool: tool as 'pencil' | 'highlighter',
          points,
          color,
          lineWidth,
          opacity,
        };
        commitAnnotation(a);
      }
      currentPathRef.current = [];
      return;
    }

    const preview = previewAnnotation;
    if (preview && preview.id === '__preview__')
    {
      const committed = { ...preview, id: uid() };
      // Check minimum size for shapes
      let valid = true;
      if (committed.kind === 'shape' || committed.kind === 'blur')
      {
        valid = Math.abs(committed.width) > 3 && Math.abs(committed.height) > 3;
      }
      if (committed.kind === 'line')
      {
        const dx = committed.x2 - committed.x1;
        const dy = committed.y2 - committed.y1;
        valid = dx * dx + dy * dy > 9;
      }
      if (valid) commitAnnotation(committed as Annotation);
      else setPreviewAnnotation(null);
    }

    startPointRef.current = null;
  }, [tool, color, lineWidth, opacity, commitAnnotation, previewAnnotation, selectedAnnotationIndex, saveHistory]);

  const onMouseLeave = useCallback(() =>
  {
    if (isPanningRef.current)
    {
      isPanningRef.current = false;
      panStartRef.current = null;
    }
    if (isMovingRef.current)
    {
      isMovingRef.current = false;
      setSelectedAnnotationIndex(null);
      moveOffsetRef.current = null;
      setPreviewAnnotation(null);
    }
    // Don't cancel drawing when mouse leaves - let it continue if mouse button is still down
    // Drawing will continue when mouse re-enters the area
  }, []);

  // ---------------------------------------------------------------------------
  // Keyboard shortcuts
  // ---------------------------------------------------------------------------

  useEffect(() =>
  {
    const handler = (e: KeyboardEvent) =>
    {
      if (e.ctrlKey && e.shiftKey && e.key === 'Z')
      {
        e.preventDefault();
        redo();
      }
      else if (e.ctrlKey && e.key === 'z')
      {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  const lineWidthRef = useRef(lineWidth);
  const opacityRef = useRef(opacity);
  const fillShapeRef = useRef(fillShape);

  useEffect(() =>
  {
    toolRef.current = tool;
    colorRef.current = color;
    lineWidthRef.current = lineWidth;
    opacityRef.current = opacity;
    fillShapeRef.current = fillShape;
  }, [tool, color, lineWidth, opacity, fillShape]);

  // Global handlers for drawing outside SVG
  useEffect(() =>
  {
    const handleGlobalMouseMove = (e: globalThis.MouseEvent) =>
    {
      const svg = svgRef.current;
      if (!svg || !isDrawingRef.current) return;

      const rawPt = getSvgPoint(e as any, svg);
      const pt = clampPoint(rawPt, imageWidth, imageHeight);
      const start = startPointRef.current;
      if (!start) return;

      const currentTool = toolRef.current;
      const currentColor = colorRef.current;
      const currentLineWidth = lineWidthRef.current;
      const currentOpacity = opacityRef.current;
      const currentFillShape = fillShapeRef.current;

      if (currentTool === 'pencil' || currentTool === 'highlighter')
      {
        currentPathRef.current = [...currentPathRef.current, pt];
        const preview: PathAnnotation = {
          id: '__preview__',
          kind: 'path',
          tool: currentTool as 'pencil' | 'highlighter',
          points: currentPathRef.current,
          color: currentColor,
          lineWidth: currentLineWidth,
          opacity: currentOpacity,
        };
        setPreviewAnnotation(preview);
      }
      else if (currentTool === 'rect' || currentTool === 'circle' || currentTool === 'censor' || currentTool === 'blur' || currentTool === 'line' || currentTool === 'arrow')
      {
        let preview: Annotation | null = null;
        if (currentTool === 'rect')
        {
          preview = {
            id: '__preview__', kind: 'shape', tool: 'rect',
            x: start.x, y: start.y,
            width: pt.x - start.x, height: pt.y - start.y,
            fill: currentFillShape, color: currentColor, lineWidth: currentLineWidth, opacity: currentOpacity,
          } as ShapeAnnotation;
        }
        else if (currentTool === 'censor')
        {
          preview = {
            id: '__preview__', kind: 'shape', tool: 'censor',
            x: start.x, y: start.y,
            width: pt.x - start.x, height: pt.y - start.y,
            fill: true, color: currentColor, lineWidth: currentLineWidth, opacity: currentOpacity,
          } as ShapeAnnotation;
        }
        else if (currentTool === 'circle')
        {
          preview = {
            id: '__preview__', kind: 'shape', tool: 'circle',
            x: start.x, y: start.y,
            width: pt.x - start.x, height: pt.y - start.y,
            fill: currentFillShape, color: currentColor, lineWidth: currentLineWidth, opacity: currentOpacity,
          } as ShapeAnnotation;
        }
        else if (currentTool === 'line')
        {
          preview = {
            id: '__preview__', kind: 'line', tool: 'line',
            x1: start.x, y1: start.y, x2: pt.x, y2: pt.y,
            color: currentColor, lineWidth: currentLineWidth, opacity: currentOpacity,
          } as LineAnnotation;
        }
        else if (currentTool === 'arrow')
        {
          preview = {
            id: '__preview__', kind: 'line', tool: 'arrow',
            x1: start.x, y1: start.y, x2: pt.x, y2: pt.y,
            color: currentColor, lineWidth: currentLineWidth, opacity: currentOpacity,
          } as LineAnnotation;
        }
        else if (currentTool === 'blur')
        {
          preview = {
            id: '__preview__', kind: 'blur', tool: 'blur',
            x: start.x, y: start.y,
            width: pt.x - start.x, height: pt.y - start.y,
            color: currentColor, lineWidth: currentLineWidth, opacity: currentOpacity,
          } as BlurAnnotation;
        }
        if (preview) setPreviewAnnotation(preview);
      }
    };

    const handleGlobalMouseUp = () =>
    {
      if (!isDrawingRef.current) return;

      // If already processed by SVG handler, skip
      if (mouseUpProcessedRef.current) return;
      mouseUpProcessedRef.current = true;

      const start = startPointRef.current;
      if (start)
      {
        const currentTool = toolRef.current;
        const currentColor = colorRef.current;
        const currentLineWidth = lineWidthRef.current;
        const currentOpacity = opacityRef.current;

        if ((currentTool === 'pencil' || currentTool === 'highlighter') && currentPathRef.current.length >= 2)
        {
          const a: PathAnnotation = {
            id: uid(),
            kind: 'path',
            tool: currentTool as 'pencil' | 'highlighter',
            points: currentPathRef.current,
            color: currentColor,
            lineWidth: currentLineWidth,
            opacity: currentOpacity,
          };
          commitAnnotation(a);
        }
        else
        {
          setPreviewAnnotation(prev =>
          {
            if (prev && prev.id === '__preview__')
            {
              const committed = { ...prev, id: uid() };
              let valid = true;
              if (committed.kind === 'shape' || committed.kind === 'blur')
              {
                valid = Math.abs(committed.width) > 3 && Math.abs(committed.height) > 3;
              }
              if (committed.kind === 'line')
              {
                const dx = committed.x2 - committed.x1;
                const dy = committed.y2 - committed.y1;
                valid = dx * dx + dy * dy > 9;
              }
              if (valid) commitAnnotation(committed as Annotation);
            }
            return null;
          });
        }
      }

      isDrawingRef.current = false;
      currentPathRef.current = [];
      startPointRef.current = null;
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () =>
    {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [imageWidth, imageHeight, commitAnnotation]);

  return {
    svgRef,
    containerRef,
    wrapperRef,
    imgRef,
    imageWidth,
    imageHeight,
    imageLoaded,
    imageSrc,
    isExporting,
    useCrossOrigin,

    tool,
    setTool,
    color,
    setColor,
    lineWidth,
    setLineWidth,
    opacity,
    setOpacity,
    fillShape,
    setFillShape,
    fontSize,
    setFontSize,
    textBold,
    setTextBold,
    textItalic,
    setTextItalic,
    textHasBackground,
    setTextHasBackground,
    textBackgroundColor,
    setTextBackgroundColor,

    annotations,
    previewAnnotation,
    selectedAnnotationIndex,

    canUndo: historyRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    undo,
    redo,
    reset,
    showAnnotations,
    toggleAnnotations,
    exportImage,
    flipHorizontal,
    flipVertical,

    zoom,
    zoomIn,
    zoomOut,
    zoomReset,
    fitToScreen,

    pinCounter,
    pinStyle,
    pinValue,
    setPinStyle,
    setPinValue,
    resetPinValue,
    textInput,
    confirmText,
    cancelText,

    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,

    flipState,
  };
}
